import axios, {AxiosError, AxiosRequestConfig} from 'axios';
import {HTTP_HOST} from 'shared/consts';
import {clientLogger} from 'shared/logger/clientLogger.ts';

const COLLECTOR_HOST = '';
const NOT_BASE_URLS = ['/fgis', '/bee-share', '/fgis-soap-native'];

export const $api = axios.create({
  baseURL: HTTP_HOST,
  withCredentials: true,
});
let refreshPromise: Promise<any> | null = null;
let isBlockedRedirectInProgress = false;

$api.interceptors.request.use((config) => {
  const url = String(config.url || '');

  if (!url.includes('http')) {
    // Если идём на /collector/* — используем другой baseURL
    if (url.startsWith('/collector')) {
      config.baseURL = COLLECTOR_HOST ? `${COLLECTOR_HOST}` : '';
    } else if (NOT_BASE_URLS.some((el) => url.includes(el))) {
      config.baseURL = '';
    } else {
      config.baseURL = HTTP_HOST;
    }
  }

  return config;
});

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _isRetry?: boolean;
}

$api.interceptors.response.use(
  (config) => config,
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosRequestConfig;

    if (isUserBlockedError(error)) {
      redirectBlockedUser();
      return Promise.reject(error);
    }
    
    if (error.response?.status === 401 && error.config && !originalRequest._isRetry) {
      const url = String(originalRequest.url || '');
      const isRefreshCall = url.includes('/oauth/realms/main/token');
      if (isRefreshCall) return Promise.reject(error);

      const origin = window.location.origin || import.meta.env.VITE_AUTH_HOST;
      const pathname = window.location.pathname || '/';
      const myHost = origin + pathname;
      const authorizePath = `${origin}/ecpp/oauth/realms/main/authorize?response_type=code&client_id=web&redirect_uri=${myHost}&frontend_url=${window.location.origin}`;

      originalRequest._isRetry = true;

      try {
        const params = {
          grant_type: 'refresh_token',
          scope: 'openid',
          redirect_uri: myHost,
          auth_mode: 'cookie',
        };

        const path = `/ecpp/oauth/realms/main/token?${new URLSearchParams(params).toString()}`;

        if (!refreshPromise) {
          refreshPromise = axios
            .post(path, undefined, {
              headers: {authorization: `basic ${btoa('web:')}`},
            })
            .finally(() => {
              refreshPromise = null;
            });
        }

        await refreshPromise;

        return axios.request(originalRequest);
      } catch {
        localStorage.clear();
        localStorage.setItem('lastPathName', pathname);
        window.open(authorizePath, '_self');
        return Promise.reject(error);
      }
    }

    try {
      const status = error.response?.status;
      const cfg = error.config;

      if (cfg && !shouldSkipApiLog(cfg)) {
        clientLogger.log({
          type: 'api',
          severity: 'error',
          message: status
            ? `HTTP ${status} ${error.response?.statusText || ''}`.trim()
            : error.message || 'Network error',
          stack: (error as any)?.stack,
          route: window.location.pathname,
          meta: sanitizeMeta({
            url: safeUrl(cfg),
            method: (cfg.method || 'GET').toUpperCase(),
            status,
            statusText: error.response?.statusText,
            requestId:
              (error.response?.headers as any)?.['x-request-id'] ||
              (error.response?.headers as any)?.['x-correlation-id'],
            responsePreview:
              typeof error.response?.data === 'string'
                ? error.response.data.slice(0, 300)
                : undefined,
          }),
        });
      }
    } catch {
      /* */
    }

    return Promise.reject(error);
  }
);

function safeUrl(config?: AxiosRequestConfig) {
  const base = config?.baseURL ?? '';
  const url = config?.url ?? '';
  return url.startsWith('http') ? url : `${base}${url}`;
}

function shouldSkipApiLog(config?: AxiosRequestConfig) {
  const url = String(config?.url || '');
  return url.includes('/client-logs') || url.startsWith('/collector');
}

function isUserBlockedError(error: AxiosError) {
  const data = error.response?.data as {code?: string} | undefined;
  return error.response?.status === 403 && data?.code === 'USER_BLOCKED';
}

function redirectBlockedUser() {
  if (isBlockedRedirectInProgress) return;

  isBlockedRedirectInProgress = true;
  const origin = window.location.origin || import.meta.env.VITE_AUTH_HOST;
  const loginUrl = `${origin}/oauth/realms/main/login?errors=account:blocked`;
  const logoutUrl = `${origin}/ecpp/oauth/realms/main/logout?redirect_uri=${encodeURIComponent(loginUrl)}`;

  localStorage.clear();

  const form = document.createElement('form');
  form.action = logoutUrl;
  form.method = 'POST';
  form.style.display = 'none';

  document.body.append(form);
  form.submit();
}

function sanitizeMeta(meta: Record<string, any>) {
  if (meta?.headers) {
    const h = {...meta.headers};
    delete h.Authorization;
    delete h.authorization;
    delete h.cookie;
    delete h.Cookie;
    meta.headers = h;
  }
  return meta;
}

export default $api;
